import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, UserPlus, Phone, Mail, Calendar, FileDown, Pencil, Trash2, MapPin, AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const SEVERITY = {
  ok:       { label: "In Compliance",      pill: "bg-emerald-50 text-emerald-800 border-emerald-300", ring: "ring-emerald-300", dot: "bg-emerald-500", Icon: CheckCircle2 },
  notice:   { label: "Action ≤ 30d",       pill: "bg-yellow-50 text-yellow-900 border-yellow-300",     ring: "ring-yellow-300", dot: "bg-yellow-500", Icon: AlertTriangle },
  warning:  { label: "Expiring ≤ 15d",     pill: "bg-amber-50 text-amber-800 border-amber-300",        ring: "ring-amber-300",  dot: "bg-amber-500",  Icon: AlertTriangle },
  critical: { label: "Expires ≤ 7d",       pill: "bg-red-50 text-red-800 border-red-300",              ring: "ring-red-300",    dot: "bg-red-500",    Icon: AlertTriangle },
  expired:  { label: "OUT OF COMPLIANCE",  pill: "bg-red-100 text-red-900 border-red-400 font-bold",   ring: "ring-red-400",    dot: "bg-red-600",    Icon: ShieldAlert },
};

const EMPTY = {
  name: "", phone: "", email: "", address: "",
  emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
  availability: "", notes: "", active: true, is_administrator: false,
  items: {},
};

export default function StaffSubstitutes() {
  const [rows, setRows] = useState([]);
  const [template, setTemplate] = useState({ items: [], rules: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | row id
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/staff/substitutes").then((r) => r.data),
      api.get("/staff/substitutes/checklist-template").then((r) => r.data),
    ])
      .then(([rs, tpl]) => { setRows(rs); setTemplate(tpl); })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (row) => { setForm({ ...EMPTY, ...row, items: row.items || {} }); setEditing(row.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (key, field, value) => setForm((f) => {
    const items = { ...(f.items || {}) };
    items[key] = { ...(items[key] || {}), [field]: value };
    if (!items[key].completed && !items[key].expires && !items[key].notes) delete items[key];
    return { ...f, items };
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/staff/substitutes", form);
        toast.success("Substitute added.");
      } else {
        await api.put(`/staff/substitutes/${editing}`, form);
        toast.success("Saved.");
      }
      close();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete substitute "${row.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/staff/substitutes/${row.id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const printProfile = async (row) => {
    try {
      const res = await api.get(`/staff/substitutes/${row.id}/profile-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `substitute-${(row.name || "profile").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;

  return (
    <div data-testid="staff-substitutes-page" className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Staff App</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Substitute Teachers</h1>
          <p className="mt-2 text-stone-500">On-call staff with full Ohio compliance tracking — same checklist as your full-time teachers.</p>
        </div>
        <Button data-testid="sub-add-btn" onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
          <UserPlus className="w-4 h-4 mr-1.5" /> Add substitute
        </Button>
      </header>

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-12 text-center">
          <p className="text-sm text-stone-500">No substitute teachers yet. Add your first one to track compliance and keep their info handy.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sub-list">
          {rows.map((s) => <SubCard key={s.id} sub={s} onEdit={() => openEdit(s)} onDelete={() => remove(s)} onPrint={() => printProfile(s)} />)}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent data-testid="sub-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Add substitute teacher" : "Edit substitute"}</DialogTitle>
            <DialogDescription>Contact, emergency contact, and full Ohio compliance checklist.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-5">
            <Section title="Identity & contact">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Full name" required>
                  <Input data-testid="sub-name" value={form.name} onChange={(e) => up("name", e.target.value)} className="h-10" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="sub-phone" value={form.phone} onChange={(e) => up("phone", e.target.value)} className="h-10" placeholder="(555)555-1234" />
                </Field>
                <Field label="Email">
                  <Input data-testid="sub-email" type="email" value={form.email} onChange={(e) => up("email", e.target.value)} className="h-10" />
                </Field>
                <Field label="Availability">
                  <Input data-testid="sub-availability" value={form.availability} onChange={(e) => up("availability", e.target.value)} className="h-10" placeholder="Mon, Tue, Thu 8a–3p" />
                </Field>
              </div>
              <Field label="Home address">
                <Input data-testid="sub-address" value={form.address} onChange={(e) => up("address", e.target.value)} className="h-10" placeholder="123 Main St, City, OH 43215" />
              </Field>
            </Section>

            <Section title="Emergency contact">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Name">
                  <Input data-testid="sub-emergency-name" value={form.emergency_contact_name} onChange={(e) => up("emergency_contact_name", e.target.value)} className="h-10" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="sub-emergency-phone" value={form.emergency_contact_phone} onChange={(e) => up("emergency_contact_phone", e.target.value)} className="h-10" />
                </Field>
                <Field label="Relation">
                  <Input data-testid="sub-emergency-relation" value={form.emergency_contact_relation} onChange={(e) => up("emergency_contact_relation", e.target.value)} className="h-10" placeholder="Spouse / Parent / …" />
                </Field>
              </div>
            </Section>

            <Section
              title="Ohio compliance checklist"
              hint="Mark each requirement complete and (where applicable) record the expiry. Same checklist as full-time staff."
            >
              <div className="space-y-2">
                {template.items.map((it) => {
                  const rule = template.rules?.[it.key] || {};
                  if (rule.admins_only && !form.is_administrator) return null;
                  const entry = form.items?.[it.key] || {};
                  const checked = !!entry.completed;
                  return (
                    <div key={it.key} data-testid={`sub-item-${it.key}`} className={`grid grid-cols-[24px_1fr_140px_140px] gap-2 items-start p-2.5 rounded-lg border ${checked ? "border-emerald-200 bg-emerald-50/30" : "border-stone-200 bg-white"}`}>
                      <input
                        type="checkbox"
                        data-testid={`sub-item-check-${it.key}`}
                        checked={checked}
                        onChange={(e) => setItem(it.key, "completed", e.target.checked ? new Date().toISOString().slice(0, 10) : "")}
                        className="mt-1.5 rounded border-stone-300 text-emerald-700 focus:ring-emerald-600"
                      />
                      <div className="text-sm text-stone-800 leading-tight pt-0.5">
                        {it.label}
                        {rule.expires && rule.expires !== "none" && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-stone-500">renew every {rule.expires}</span>
                        )}
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-0.5">Completed</div>
                        <Input
                          type="date"
                          data-testid={`sub-item-completed-${it.key}`}
                          value={entry.completed || ""}
                          onChange={(e) => setItem(it.key, "completed", e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-0.5">Expires</div>
                        <Input
                          type="date"
                          data-testid={`sub-item-expires-${it.key}`}
                          value={entry.expires || ""}
                          onChange={(e) => setItem(it.key, "expires", e.target.value)}
                          className="h-8 text-xs"
                          disabled={!rule.expires || rule.expires === "none"}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Other">
              <Field label="Notes">
                <Textarea data-testid="sub-notes" value={form.notes} onChange={(e) => up("notes", e.target.value)} rows={3} />
              </Field>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-stone-700">
                  <input
                    type="checkbox"
                    data-testid="sub-active"
                    checked={!!form.active}
                    onChange={(e) => up("active", e.target.checked)}
                    className="rounded border-stone-300 text-emerald-700 focus:ring-emerald-600"
                  />
                  Active (available for shifts)
                </label>
                <label className="flex items-center gap-2 text-sm text-stone-700">
                  <input
                    type="checkbox"
                    data-testid="sub-is-admin"
                    checked={!!form.is_administrator}
                    onChange={(e) => up("is_administrator", e.target.checked)}
                    className="rounded border-stone-300 text-emerald-700 focus:ring-emerald-600"
                  />
                  Administrator (unlocks admin-only checklist items)
                </label>
              </div>
            </Section>

            <DialogFooter className="pt-2 sticky bottom-0 bg-white">
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button type="submit" data-testid="sub-save-btn" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubCard({ sub, onEdit, onDelete, onPrint }) {
  const status = sub.status || { overall: "ok", warnings: [] };
  const meta = SEVERITY[status.overall] || SEVERITY.ok;
  const StatusIcon = meta.Icon;
  return (
    <article data-testid={`sub-card-${sub.id}`} className={`rounded-2xl border bg-white p-5 space-y-2 ring-1 ${meta.ring}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-lg font-semibold text-stone-900">{sub.name}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${meta.pill}`}>
              <StatusIcon className="w-3 h-3" /> {meta.label}
            </span>
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${sub.active ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
              {sub.active ? "Active" : "Inactive"}
            </span>
            {sub.is_administrator && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Admin</span>}
          </div>
        </div>
        <div className="flex gap-1">
          <Button type="button" data-testid={`sub-edit-${sub.id}`} variant="ghost" className="h-8 w-8 p-0" onClick={onEdit} title="Edit">
            <Pencil className="w-3.5 h-3.5 text-stone-500" />
          </Button>
          <Button type="button" data-testid={`sub-delete-${sub.id}`} variant="ghost" className="h-8 w-8 p-0" onClick={onDelete} title="Delete">
            <Trash2 className="w-3.5 h-3.5 text-rose-500" />
          </Button>
        </div>
      </div>
      <Row icon={Phone} value={sub.phone} />
      <Row icon={Mail} value={sub.email} />
      <Row icon={MapPin} value={sub.address} />
      <Row icon={Calendar} value={sub.availability} />
      {(sub.emergency_contact_name || sub.emergency_contact_phone) && (
        <div className="text-[11px] text-stone-500 border-t border-stone-100 pt-2">
          <b className="text-stone-600">Emergency:</b> {sub.emergency_contact_name || "—"} {sub.emergency_contact_phone ? `· ${sub.emergency_contact_phone}` : ""} {sub.emergency_contact_relation ? `(${sub.emergency_contact_relation})` : ""}
        </div>
      )}
      {(status.warnings || []).length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1" data-testid={`sub-warnings-${sub.id}`}>
          {status.warnings.slice(0, 4).map((w, i) => {
            const sv = SEVERITY[w.severity] || SEVERITY.warning;
            return (
              <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-md border ${sv.pill}`}>
                {w.key.replace(/_/g, " ")} · {w.days < 0 ? `${Math.abs(w.days)}d overdue` : `${w.days}d`}
              </span>
            );
          })}
        </div>
      )}
      {sub.notes && <p className="text-[12px] text-stone-600 border-t border-stone-100 pt-2 leading-relaxed">{sub.notes}</p>}
      <div className="pt-2">
        <Button type="button" data-testid={`sub-print-${sub.id}`} variant="outline" className="w-full rounded-xl text-xs" onClick={onPrint}>
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> Print profile + compliance PDF
        </Button>
      </div>
    </article>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className="space-y-2.5">
      <header>
        <h4 className="font-display text-sm font-bold text-stone-900 uppercase tracking-widest">{title}</h4>
        {hint && <p className="text-[11px] text-stone-500 mt-0.5">{hint}</p>}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-stone-500">
        {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm text-stone-700">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-stone-400 shrink-0" />
      <span className="leading-snug">{value}</span>
    </div>
  );
}
